<template>
  <b-modal
    id="modal-bulk-upload-imagem"
    v-model="show"
    title="Upload em massa de fotos"
    size="lg"
    centered
    no-close-on-backdrop
    :no-close-on-esc="loading"
    :hide-header-close="loading"
    hide-footer
    @hide="onHide"
    @hidden="onHidden"
  >
    <!-- Loading: enviando/processando o envio real -->
    <div v-if="loading" class="text-center py-2">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
      <h5 class="mt-2 mb-1">{{ fase === 'processando' ? 'Processando no servidor...' : 'Enviando fotos...' }}</h5>
      <p class="text-muted mb-1">
        <span v-if="fase === 'enviando'">
          Enviando {{ arquivos.length }} imagem(ns). Não feche esta janela.
        </span>
        <span v-else>
          As fotos foram enviadas. O servidor está otimizando e cadastrando as imagens.
          Isso pode levar alguns instantes — não feche esta janela.
        </span>
      </p>
      <b-progress
        :value="fase === 'processando' ? 100 : progresso"
        :max="100"
        :animated="fase === 'processando'"
        :striped="fase === 'processando'"
        :variant="fase === 'processando' ? 'info' : 'primary'"
        :show-progress="fase === 'enviando'"
        height="1.2rem"
        class="mx-auto"
        style="max-width: 420px;"
      />
      <small v-if="fase === 'enviando'" class="text-muted d-block mt-50">{{ progresso }}% enviado</small>
    </div>

    <!-- Etapa 1: seleção da pasta -->
    <div v-else-if="!preview && !relatorio">
      <b-alert variant="info" show class="mb-2">
        <div class="alert-body">
          <p class="mb-1">
            Selecione uma <strong>pasta</strong> com as fotos. O nome de cada arquivo deve ser o
            <strong>código interno do produto</strong> (ISBN/EAN).
          </p>
          <ul class="mb-0 pl-3">
            <li><code>9780230010086.png</code> &rarr; imagem principal</li>
            <li><code>9780230010086-1.png</code>, <code>9780230010086-2.png</code> &rarr; secundárias</li>
          </ul>
          <p class="mb-0 mt-1">
            Se o produto <strong>já tiver imagens</strong>, elas serão <strong>substituídas</strong> pelas novas.
            Você confere o que será criado e substituído antes de confirmar.
          </p>
        </div>
      </b-alert>

      <b-form-group label="Pasta de fotos" label-for="bulk-folder-input">
        <input
          id="bulk-folder-input"
          ref="folderInput"
          type="file"
          class="form-control"
          webkitdirectory
          directory
          multiple
          accept="image/*"
          :disabled="analisando"
          @change="onSelect"
        >
        <small class="text-muted">
          Caso o navegador não permita selecionar pasta, você pode selecionar vários arquivos.
        </small>
      </b-form-group>

      <div v-if="arquivos.length" class="mb-2">
        <b-badge variant="light-primary">
          {{ arquivos.length }} imagem(ns) selecionada(s)
        </b-badge>
      </div>

      <div class="d-flex justify-content-end">
        <b-button variant="outline-secondary" class="mr-1" :disabled="analisando" @click="show = false">
          Cancelar
        </b-button>
        <b-button variant="primary" :disabled="analisando || !arquivos.length" @click="analisar">
          <b-spinner v-if="analisando" small class="mr-50" />
          <feather-icon v-else icon="SearchIcon" class="mr-50" />
          Analisar {{ arquivos.length ? `(${arquivos.length})` : '' }}
        </b-button>
      </div>
    </div>

    <!-- Etapa 2: relatório de análise (confirmar antes de enviar) -->
    <div v-else-if="preview && !relatorio">
      <h5 class="mb-1">Confira as alterações</h5>
      <b-row class="mb-1">
        <b-col cols="6" md="3" class="mb-1">
          <b-card no-body class="border text-center mb-0 p-1">
            <h3 class="mb-0 text-success">{{ preview.resumo.novos }}</h3>
            <small class="text-muted">Novos</small>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="mb-1">
          <b-card no-body class="border text-center mb-0 p-1">
            <h3 class="mb-0 text-warning">{{ preview.resumo.substituir }}</h3>
            <small class="text-muted">Substituições</small>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="mb-1">
          <b-card no-body class="border text-center mb-0 p-1">
            <h3 class="mb-0 text-danger">{{ preview.resumo.naoEncontrados }}</h3>
            <small class="text-muted">Não encontrados</small>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="mb-1">
          <b-card no-body class="border text-center mb-0 p-1">
            <h3 class="mb-0">{{ preview.totalArquivos }}</h3>
            <small class="text-muted">Arquivos</small>
          </b-card>
        </b-col>
      </b-row>

      <b-alert v-if="preview.resumo.substituir" variant="warning" show class="mb-1">
        <div class="alert-body">
          <feather-icon icon="AlertTriangleIcon" class="mr-50" />
          <strong>{{ preview.resumo.substituir }}</strong> produto(s) já possuem imagens —
          as imagens atuais serão <strong>substituídas</strong> pelas novas.
        </div>
      </b-alert>

      <div class="border rounded" style="max-height: 320px; overflow-y: auto;">
        <b-table-simple small responsive class="mb-0">
          <b-thead head-variant="light">
            <b-tr>
              <b-th>Código</b-th>
              <b-th>Produto</b-th>
              <b-th class="text-center">Situação</b-th>
              <b-th class="text-center">Atuais</b-th>
              <b-th class="text-center">Novas</b-th>
            </b-tr>
          </b-thead>
          <b-tbody>
            <b-tr v-for="(p, i) in preview.produtos" :key="`prev-${i}`">
              <b-td><code>{{ p.codigo }}</code></b-td>
              <b-td>
                <span v-if="p.status !== 'nao_encontrado'">
                  {{ p.familia_descricao }}
                  <small class="text-muted d-block">{{ p.marca }}</small>
                </span>
                <small v-else class="text-muted">{{ p.motivo || 'Sem produto correspondente' }}</small>
              </b-td>
              <b-td class="text-center">
                <b-badge :variant="statusVariant(p.status)">{{ statusLabel(p.status) }}</b-badge>
              </b-td>
              <b-td class="text-center">
                <span :class="p.imagensExistentes ? 'text-warning font-weight-bold' : 'text-muted'">
                  {{ p.imagensExistentes }}
                </span>
              </b-td>
              <b-td class="text-center">{{ p.imagensNovas }}</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-1">
        <small v-if="preview.resumo.naoEncontrados" class="text-danger">
          {{ preview.resumo.naoEncontrados }} código(s) sem produto serão ignorados.
        </small>
        <span v-else />
        <div>
          <b-button variant="outline-secondary" class="mr-1" @click="voltar">
            Voltar
          </b-button>
          <b-button
            variant="primary"
            :disabled="!enviaveis"
            @click="enviar"
          >
            <feather-icon icon="UploadIcon" class="mr-50" />
            Confirmar e enviar {{ enviaveis ? `(${enviaveis})` : '' }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Etapa 3: relatório do envio -->
    <div v-else>
      <h5 class="mb-1">Relatório do envio</h5>
      <b-row class="mb-2">
        <b-col cols="6" md="3" class="mb-1">
          <b-card no-body class="border text-center mb-0 p-1">
            <h3 class="mb-0 text-success">{{ relatorio.produtosProcessados }}</h3>
            <small class="text-muted">Produtos</small>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="mb-1">
          <b-card no-body class="border text-center mb-0 p-1">
            <h3 class="mb-0 text-success">{{ relatorio.imagensCriadas }}</h3>
            <small class="text-muted">Imagens criadas</small>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="mb-1">
          <b-card no-body class="border text-center mb-0 p-1">
            <h3 class="mb-0 text-warning">{{ relatorio.imagensSubstituidas || 0 }}</h3>
            <small class="text-muted">Substituídas</small>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="mb-1">
          <b-card no-body class="border text-center mb-0 p-1">
            <h3 class="mb-0 text-danger">{{ relatorio.naoEncontrados.length }}</h3>
            <small class="text-muted">Não encontrados</small>
          </b-card>
        </b-col>
      </b-row>

      <b-alert v-if="relatorio.naoEncontrados.length" variant="warning" show>
        <div class="alert-body">
          <strong>Códigos sem produto correspondente:</strong>
          <div class="mt-50">
            <b-badge
              v-for="cod in relatorio.naoEncontrados"
              :key="`nf-${cod}`"
              variant="light-warning"
              class="mr-50 mb-50"
            >
              {{ cod }}
            </b-badge>
          </div>
        </div>
      </b-alert>

      <b-alert v-if="relatorio.erros.length" variant="danger" show>
        <div class="alert-body">
          <strong>Erros:</strong>
          <ul class="mb-0 mt-50 pl-3">
            <li v-for="(err, i) in relatorio.erros" :key="`err-${i}`">
              <code>{{ err.codigo }}</code> — {{ err.mensagem }}
            </li>
          </ul>
        </div>
      </b-alert>

      <b-alert
        v-if="!relatorio.naoEncontrados.length && !relatorio.erros.length"
        variant="success"
        show
      >
        <div class="alert-body">
          Todas as fotos foram cadastradas com sucesso.
        </div>
      </b-alert>

      <div class="d-flex justify-content-end mt-1">
        <b-button variant="outline-secondary" class="mr-1" @click="resetar">
          Enviar mais
        </b-button>
        <b-button variant="primary" @click="concluir">
          Concluir
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import {
  BModal, BAlert, BFormGroup, BButton, BBadge, BRow, BCol, BCard, BSpinner, BProgress,
  BTableSimple, BThead, BTbody, BTr, BTh, BTd,
} from 'bootstrap-vue';
import TemplateImagemService from '@/services/SistemaDKP/sls-template-imagem.service';
import Notifier from '@/libs/Notifier';

export default {
  components: {
    BModal, BAlert, BFormGroup, BButton, BBadge, BRow, BCol, BCard, BSpinner, BProgress,
    BTableSimple, BThead, BTbody, BTr, BTh, BTd,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      service: new TemplateImagemService(),
      notify: new Notifier(),
      arquivos: [],
      loading: false,
      analisando: false,
      preview: null,
      relatorio: null,
      progresso: 0,
      fase: null,
    };
  },
  computed: {
    show: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    // Quantidade de imagens que serão de fato enviadas (exclui as de códigos não encontrados).
    enviaveis() {
      if (!this.preview) return 0;
      return this.preview.produtos
        .filter((p) => p.status !== 'nao_encontrado')
        .reduce((acc, p) => acc + (p.imagensNovas || 0), 0);
    },
  },
  methods: {
    onSelect(event) {
      const files = Array.from(event.target.files || []);
      this.arquivos = files.filter((f) => f.type && f.type.startsWith('image/'));
      this.preview = null;
    },
    statusLabel(status) {
      const map = { novo: 'Novo', substituir: 'Substituir', nao_encontrado: 'Não encontrado' };
      return map[status] || status;
    },
    statusVariant(status) {
      const map = { novo: 'light-success', substituir: 'light-warning', nao_encontrado: 'light-danger' };
      return map[status] || 'light-secondary';
    },
    async analisar() {
      if (!this.arquivos.length) return;
      this.analisando = true;
      try {
        this.preview = await this.service.bulkUploadPreview(this.arquivos);
      } catch (error) {
        this.notify.error(error.message || 'Erro ao analisar as imagens');
      } finally {
        this.analisando = false;
      }
    },
    voltar() {
      this.preview = null;
    },
    async enviar() {
      if (!this.arquivos.length) return;
      this.loading = true;
      this.fase = 'enviando';
      this.progresso = 0;
      try {
        const onUploadProgress = (event) => {
          if (event.total) {
            this.progresso = Math.min(100, Math.max(0, Math.round((event.loaded * 100) / event.total)));
            // Upload concluído: o servidor agora otimiza/cadastra as imagens.
            if (this.progresso >= 100) {
              this.fase = 'processando';
            }
          }
        };
        const result = await this.service.bulkUpload(this.arquivos, onUploadProgress);
        this.relatorio = result;
        this.preview = null;
        const substituidas = result.imagensSubstituidas
          ? ` (${result.imagensSubstituidas} substituída(s))`
          : '';
        this.notify.success(
          `${result.imagensCriadas} imagem(ns) cadastrada(s) em ${result.produtosProcessados} produto(s)${substituidas}.`,
        );
      } catch (error) {
        this.notify.error(error.message || 'Erro ao enviar as imagens');
      } finally {
        this.loading = false;
        this.fase = null;
        this.progresso = 0;
      }
    },
    resetar() {
      this.relatorio = null;
      this.preview = null;
      this.arquivos = [];
      this.fase = null;
      this.progresso = 0;
      if (this.$refs.folderInput) {
        this.$refs.folderInput.value = '';
      }
    },
    concluir() {
      const houveCriacao = this.relatorio && this.relatorio.imagensCriadas > 0;
      this.show = false;
      if (houveCriacao) {
        this.$emit('uploaded');
      }
    },
    onHide(bvEvent) {
      // Impede fechar (X, ESC, backdrop, clique fora) enquanto o envio está em andamento.
      if (this.loading) {
        bvEvent.preventDefault();
        this.notify.info('Aguarde o envio das fotos ser concluído antes de fechar.');
      }
    },
    onHidden() {
      this.resetar();
    },
  },
};
</script>
